import * as THREE from 'three';
import type { SketchLineObject } from '../../../mesh/sketch/SketchLineObject';
import type { ProjectionResult } from '../model/ProjectionResult';
import type {
	ReferenceDrivenLineRelationRuntimeRecord,
	LineReferenceProjectionState,
	SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import { resolveSourceRefEntityId } from '../model/SketchEntityReferenceRecord';
import {
	createExecutionDiagnostic,
	type ExecutionDiagnostic,
} from '../model/SketchEntityReferenceDiagnostics';
import { BaseReferenceDrivenRelationService } from './BaseReferenceDrivenRelationService';
import { ReferenceDrivenLineCollinearConstraint } from './constraints/ReferenceDrivenLineCollinearConstraint';
import type {
	ReferenceDrivenLineRelationBinding,
	ReferenceDrivenLineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenLineRelationService';

export type {
	ReferenceDrivenLineRelationAttachResult,
} from '../../../../types/reference/sketch-entity-reference/ReferenceDrivenLineRelationService';

/** 线投影方向判定的最小长度阈值。 */
const LINE_DIRECTION_EPSILON = 1e-8;

/**
 * 解析投影线的单位方向向量。
 *
 * @param projectedStartWorld 投影线起点。
 * @param projectedEndWorld 投影线终点。
 * @returns 投影线单位方向。
 */
function resolveProjectedLineDirection(
	projectedStartWorld: THREE.Vector3,
	projectedEndWorld: THREE.Vector3,
): THREE.Vector3 | null {
	const directionWorld = projectedEndWorld.clone().sub(projectedStartWorld);
	const directionLength = directionWorld.length();
	if (directionLength <= LINE_DIRECTION_EPSILON) {
		return null;
	}

	return directionWorld.divideScalar(directionLength);
}

/**
 * 计算点在线载体上的有符号标量。
 *
 * @param pointWorld 目标点。
 * @param lineOriginWorld 线起点。
 * @param lineDirectionWorld 线单位方向。
 * @returns 有符号标量。
 */
function resolveSignedScalarOnProjectedLine(
	pointWorld: THREE.Vector3,
	lineOriginWorld: THREE.Vector3,
	lineDirectionWorld: THREE.Vector3,
): number {
	return pointWorld.clone().sub(lineOriginWorld).dot(lineDirectionWorld);
}

/**
 * 判断两个世界坐标点是否可视为同一点。
 *
 * @param left 第一个世界坐标点。
 * @param right 第二个世界坐标点。
 * @returns 距离小于阈值时返回 true。
 */
function areWorldPointsNear(
	left: THREE.Vector3,
	right: THREE.Vector3,
): boolean {
	return left.distanceToSquared(right) <= LINE_DIRECTION_EPSILON * LINE_DIRECTION_EPSILON;
}

/**
 * 解析 topology-edge 直线引用的附着模式。
 *
 * 设计说明：
 * 1. topology-edge 代表从模型/特征拓扑边转换出来的草图实体；
 * 2. 如果目标线两端与当前投影端点重合，说明它是整边转换，后续重建必须完整跟随端点；
 * 3. 如果两端只落在同一投影载体上但不等于端点，则保留为局部参数段。
 *
 * @param targetLine 目标草图直线。
 * @param projectedStartWorld 当前投影起点。
 * @param projectedEndWorld 当前投影终点。
 * @returns line 投影附着模式。
 */
function resolveTopologyLineAttachmentMode(
	targetLine: SketchLineObject,
	projectedStartWorld: THREE.Vector3,
	projectedEndWorld: THREE.Vector3,
): NonNullable<LineReferenceProjectionState['attachmentMode']> {
	const targetStartWorld = targetLine.startPoint.getPosition();
	const targetEndWorld = targetLine.endPoint.getPosition();
	const matchesForward = areWorldPointsNear(targetStartWorld, projectedStartWorld)
		&& areWorldPointsNear(targetEndWorld, projectedEndWorld);
	const matchesBackward = areWorldPointsNear(targetStartWorld, projectedEndWorld)
		&& areWorldPointsNear(targetEndWorld, projectedStartWorld);

	return matchesForward || matchesBackward ? 'full-entity' : 'partial-range';
}

/**
 * 生成直线引用的投影状态。
 *
 * @param targetLine 目标直线。
 * @param projectedStartWorld 投影直线起点。
 * @param projectedEndWorld 投影直线终点。
 * @returns 直线投影状态。
 */
function createLineProjectionState(
	targetLine: SketchLineObject,
	projectedStartWorld: THREE.Vector3,
	projectedEndWorld: THREE.Vector3,
	reference: SketchEntityReferenceRecord,
): LineReferenceProjectionState | null {
	const lineDirectionWorld = resolveProjectedLineDirection(projectedStartWorld, projectedEndWorld);
	if (!lineDirectionWorld) {
		return null;
	}

	const startScalar = resolveSignedScalarOnProjectedLine(
		targetLine.startPoint.getPosition(),
		projectedStartWorld,
		lineDirectionWorld,
	);
	const endScalar = resolveSignedScalarOnProjectedLine(
		targetLine.endPoint.getPosition(),
		projectedStartWorld,
		lineDirectionWorld,
	);

	const projectionState: LineReferenceProjectionState = {
		projectionResultKind: 'line',
		startScalar,
		endScalar,
	};
	if (reference.sourceDescriptor.kind === 'topology-edge') {
		projectionState.attachmentMode = resolveTopologyLineAttachmentMode(
			targetLine,
			projectedStartWorld,
			projectedEndWorld,
		);
	}

	return projectionState;
}

/**
 * 参考驱动直线关系服务。
 */
export class ReferenceDrivenLineRelationService
	extends BaseReferenceDrivenRelationService<
		ReferenceDrivenLineRelationRuntimeRecord,
		ReferenceDrivenLineRelationBinding
	> {
	/**
	 * 挂载直线共线约束。
	 *
	 * @param input 挂载输入。
	 * @returns 挂载结果。
	 */
	public attach(input: {
		reference: SketchEntityReferenceRecord;
		projectionResult: Extract<ProjectionResult, { projectionResultKind: 'line' }>;
		targetLine: SketchLineObject;
		runtimeSourceName?: string;
	}): ReferenceDrivenLineRelationAttachResult {
		this.detach({
			referenceId: input.reference.referenceId,
			targetLine: input.targetLine,
		});

		const projectedStartWorld = new THREE.Vector3(...input.projectionResult.geometryPayload.startWorld);
		const projectedEndWorld = new THREE.Vector3(...input.projectionResult.geometryPayload.endWorld);
		if (!resolveProjectedLineDirection(projectedStartWorld, projectedEndWorld)) {
			return {
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'projection-degenerate',
						level: 'error',
						message: `直线引用关系挂载失败，投影直线退化，无法计算投影标量: ${input.reference.referenceId}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.targetLine.persistentId,
					}),
				],
			};
		}

		const record: ReferenceDrivenLineRelationRuntimeRecord = {
			referenceId: input.reference.referenceId,
			relationKind: 'collinear',
			driveMode: 'projection-drives-target',
			sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
			targetEntityId: input.targetLine.persistentId,
			constraintId: -1,
		};
		const constraint = new ReferenceDrivenLineCollinearConstraint({
			referenceId: input.reference.referenceId,
			projectedStartWorld,
			projectedEndWorld,
			sourceDisplayLabel: input.reference.sourceDisplayLabel,
			runtimeSourceName: input.runtimeSourceName,
		});
		input.targetLine.constraints.addRelation(constraint);
		record.constraintId = constraint.id;
		this.setBinding(input.reference.referenceId, {
			constraint,
			record,
			targetLine: input.targetLine,
		});

		try {
			/**
			 * 挂载阶段只冻结局部段分支，不直接修改目标几何。
			 * 外部无限线公式会在批次统一参数求解时与其他关系一起分配自由度。
			 */
			record.projectionState = createLineProjectionState(
				input.targetLine,
				projectedStartWorld,
				projectedEndWorld,
				input.reference,
			);
			if (!record.projectionState) {
				input.targetLine.constraints.removeRelation(constraint.id);
				this.deleteBinding(input.reference.referenceId);
				return {
					diagnostics: [
						createExecutionDiagnostic({
							kind: 'projection-degenerate',
							level: 'error',
							message: `直线引用关系挂载失败，投影直线退化，无法计算投影状态: ${input.reference.referenceId}`,
							referenceId: input.reference.referenceId,
							sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
							targetEntityId: input.targetLine.persistentId,
						}),
					],
				};
			}
		} catch (error) {
			input.targetLine.constraints.removeRelation(constraint.id);
			this.deleteBinding(input.reference.referenceId);
			return {
				diagnostics: [
					createExecutionDiagnostic({
						kind: 'restore-failed',
						level: 'error',
						message: `直线引用关系挂载失败: ${error instanceof Error ? error.message : String(error)}`,
						referenceId: input.reference.referenceId,
						sourceRefEntityId: resolveSourceRefEntityId(input.reference.sourceDescriptor),
						targetEntityId: input.targetLine.persistentId,
					}),
				],
			};
		}
		return {
			record,
			diagnostics: [],
		};
	}

	/**
	 * 拆除直线关系。
	 *
	 * @param input 拆除输入。
	 * @returns 诊断信息。
	 */
	public detach(input: {
		referenceId: string;
		targetLine?: SketchLineObject;
	}): ExecutionDiagnostic[] {
		const binding = this.getBinding(input.referenceId);
		if (binding) {
			binding.targetLine.constraints.removeRelation(binding.record.constraintId);
			this.deleteBinding(input.referenceId);
			return [];
		}

		if (input.targetLine) {
			const constraint = input.targetLine.constraints.getRelations().find((candidate) => (
				candidate instanceof ReferenceDrivenLineCollinearConstraint
				&& candidate.referenceId === input.referenceId
			));
			if (constraint) {
				input.targetLine.constraints.removeRelation(constraint.id);
			}
		}
		return [];
	}
}
